import React, { createElement } from 'react';
import ReactDOMServer from 'react-dom/server';
import { IntlProvider, addLocaleData, Locale } from 'react-intl';

import _padStart from 'lodash/padStart';

import { FALLBACK_LANGUAGE, NAME_FIRST_LANGUAGES, REACT_INTL_LOCALE_MAPPING } from './constants';

/**
 * мы не можем мигрировать на новую версию react-intl
 * (которая реализует типы сама), потому что
 * новые версии react-intl построены на другом
 * синтаксисе форматирования
 *
 * у нас накопились огромные массивы переводов и
 * переделать их все на новый синтаксис - очень
 * большая и сложная задача
 *
 * @types/react-intl был объявлен устаревшим
 * но мы используем его по причине описанной выше
 */

export type Values = Record<string, string | number | null>;
export type HTMLValues = {
  [key in keyof Values]: Values[key] | React.ReactElement;
};

export type MessageDescriptor = {
  id: string;
  defaultMessage?: string;
};

export type ExtendedSelf = Window &
  typeof globalThis & {
    tw?: {
      language: string;
    };
  };

export type ExtendedGlobal = typeof globalThis & {
  __lang?: string;
  __l10n: Record<string, string>;
};

// eslint-disable-next-line no-underscore-dangle
const __CLIENT__ = typeof window !== 'undefined';

const localeData: Record<string, Locale> = {
  de: require('react-intl/locale-data/de'),
  'en-ie': require('react-intl/locale-data/en'),
  es: require('react-intl/locale-data/es'),
  ru: require('react-intl/locale-data/ru'),
  'ru-ua': require('react-intl/locale-data/ru'),
  'uk-ua': require('react-intl/locale-data/uk'),
  'en-gb': require('react-intl/locale-data/en'),
  'en-us': require('react-intl/locale-data/en'),
  pl: require('react-intl/locale-data/pl'),
  'ru-kz': require('react-intl/locale-data/ru'),
  kz: require('react-intl/locale-data/kk'),
  tr: require('react-intl/locale-data/tr'),
  'ru-by': require('react-intl/locale-data/ru'),
  'fr-ch': require('react-intl/locale-data/fr'),
  'en-ch': require('react-intl/locale-data/en'),
  'de-ch': require('react-intl/locale-data/de'),
  fr: require('react-intl/locale-data/fr'),
  'pt-br': require('react-intl/locale-data/pt'),
};

export const getLang = (): keyof typeof localeData => {
  const currentLanguage = __CLIENT__
    ? // eslint-disable-next-line no-restricted-globals
      self.document.documentElement.lang || (self as ExtendedSelf).tw?.language || ''
    : (global as ExtendedGlobal).__lang || '';

  return localeData[currentLanguage] ? currentLanguage : FALLBACK_LANGUAGE;
};

export const lang = getLang();

addLocaleData(localeData[lang]);

export const is12hoursClock = __SOLAR__ ? false : lang === 'en-us';
export const isMonthFirst = lang === 'en-us';
export const isNameFirst = NAME_FIRST_LANGUAGES.includes(lang);
export const langCode = lang.substring(0, 2);

const getIntl = () => {
  const locale = REACT_INTL_LOCALE_MAPPING[getLang()];

  const props = {
    locale,
    messages: (global as ExtendedGlobal).__l10n,
    defaultLocale: locale,
  };

  const intlProvider = new IntlProvider(props, {});

  if (!intlProvider.context) {
    intlProvider.context = {};
  }

  const { intl } = intlProvider.getChildContext();

  return intl;
};

let intl = getIntl();

export const formatMessage = (
  messageDescriptor: MessageDescriptor,
  rawValues: Values = {}
): string => {
  if (!__CLIENT__) {
    intl = getIntl();
  }

  const escapedValues = Object.keys(rawValues).reduce((escaped: Values, name: string) => {
    const value = rawValues[name];

    if (value === null || value === undefined) {
      return {
        ...escaped,
        [name]: '',
      };
    }

    return {
      ...escaped,
      [name]: String(value),
    };
  }, {});

  return intl.formatMessage(messageDescriptor, escapedValues);
};

export const formatHTMLMessage = (
  messageDescriptor: MessageDescriptor,
  rawValues: HTMLValues = {}
): string => {
  if (!__CLIENT__) {
    intl = getIntl();
  }

  const escapedValues = Object.keys(rawValues).reduce((escaped: HTMLValues, name: string) => {
    const value = rawValues[name];

    if (value && typeof value === 'object') {
      return {
        ...(escaped as Values),
        [name]: ReactDOMServer.renderToString(value),
      };
    }

    if (value === null || value === undefined) {
      return {
        ...(escaped as Values),
        [name]: '',
      };
    }

    return {
      ...(escaped as Values),
      [name]: String(value),
    };
  }, {});

  return intl.formatMessage(messageDescriptor, escapedValues);
};

export const l10n = (id: string, values?: Values): string => {
  if (!(global as ExtendedGlobal).__l10n[id]) {
    return '';
  }

  if (values) {
    return formatMessage({ id }, values);
  }

  return (global as ExtendedGlobal).__l10n[id];
};

export const l10narray = (id: string, separator = ';'): string[] => {
  /**
   * the number of keys may differ in different locales
   * so we store arrays as semicolon separated (by default) strings
   */

  if (!id) {
    return [];
  }

  return l10n(id).split(separator);
};

// 15:10 vs 3:10 PM
type L10nTimeFunc = {
  (hour: string | number, minutes: string | number): string | null;
  (time: string): string | null;
};

export const l10ntime: L10nTimeFunc = (arg1: any, arg2?: any): string | null => {
  let hours = Number(arg1);
  let minutes = Number(arg2);

  if (arg2 === undefined) {
    if (arg1.length !== 4) {
      return null;
    }

    minutes = Number(arg1.substring(2));
    hours = Number(arg1.substring(0, 2));
  }

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  if (!is12hoursClock) {
    return `${_padStart(String(hours), 2, '0')}:${_padStart(String(minutes), 2, '0')}`;
  }

  const isPM = hours >= 12;

  if (hours > 12) {
    hours -= 12;
  }

  if (hours === 0) {
    hours = 12;
  }

  return `${_padStart(String(hours), 2, '0')}:${_padStart(String(minutes), 2, '0')}\u202F${
    isPM ? 'PM' : 'AM'
  }`;
};

export const l10nhtml = (id: string, values?: HTMLValues): string | React.ReactNode => {
  if (!(global as ExtendedGlobal).__l10n[id]) {
    return '';
  }

  return createElement('span', {
    dangerouslySetInnerHTML: {
      __html: values ? formatHTMLMessage({ id }, values) : (global as ExtendedGlobal).__l10n[id],
    },
  });
};

export { REACT_INTL_LOCALE_MAPPING } from './constants';
